import { onBoardingFormGuid } from '@/environment';
import { ChameleonEvent, useChameleon } from '@/pages/video-library/components/ChameleonProvider';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { submitForm } from '@/services/hubspot';
import { InitialState } from '@/state/user-settings/constants';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import styled from '@emotion/styled';
import { Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import BottomDotsSvg from '../../images/BottomDots.svg';
import TopDotsSvg from '../../images/TopDots.svg';
import Header from './components/Header';
import ProgressStepper from './components/ProgressStepper';
import Purpose from './components/steps/Purpose';
import Step from './components/steps/Step';
import StepsContainer from './components/steps/StepsContainer';
import { getFlow } from './OnboradingFlows';
import { OnboardingClass, OnboardingFlows, purposeStep } from './steps';

const TopDots = styled.img`
    position: fixed;
    top: 0;
    right: 0;
`;

const BottomDots = styled.img`
    position: fixed;
    bottom: 0;
    left: 0;
`;

const Container = styled.div`
    height: 100%;
    width: 100%;
    padding: 10vh 10vh;
    display: flex;
    flex-direction: column;
    justify-content: start;

    overflow-x: hidden;
    flex: 1;
    background: #fff;

    @media (max-width: 600px) {
        padding: 5% 8%;
    }
`;
interface RState {
    state: {
        [OnboardingFlows.Class]: OnboardingClass;
        [key: string]: any;
    };
    step: number;
}

const reducer = (state: RState, action: any) => {
    switch (action.type) {
        case 'loadState':
            return { state: { ...action.payload.state }, step: action.payload.step };
        case 'nextStep':
            return { ...state, step: state.step + 1 };
        case 'prevStep':
            return { ...state, step: state.step - 1 };
        default:
            if (Object.values(OnboardingFlows).includes(action.type)) {
                return { state: { ...state.state, [action.type]: action.payload }, step: state.step };
            } else {
                throw new Error('Invalid action type');
            }
    }
};

export default function Onboarding({ isExistingUser }: { isExistingUser?: boolean }) {
    const { updateUserSettings, userSettings, setShouldOnBoardingDialogBeOpen } = useUserSettings();
    const { user } = useAuth();
    const [{ state, step }, dispatch] = useReducer<Reducer<RState, any>>(reducer, { step: 0, state: InitialState() } as RState);

    const flow = useMemo(() => getFlow(state[OnboardingFlows.Class], state, user?.email), [state]);
    const mixpanel = useAnalytics();
    const [customInput, setCustomInput] = useState<string>('');
    const chameleon = useChameleon();

    const finalizeOnBoarding = useCallback(
        (done: boolean = false) => {
            const oldState = userSettings?.features?.onBoarding?.answers?.state;
            const payload = done ? { ...oldState, ...state } : {};
            mixpanel?.people.set({ ...payload, onboard_new_done: done, completed_at: new Date() });

            if (done && !!user?.email) {
                submitForm(
                    onBoardingFormGuid,
                    { email: user.email },
                    Object.keys(payload).map(k => ({ eventName: k, eventValue: payload[k] }))
                );
            }

            const mergedAnswersState = {
                ...userSettings?.features?.onBoarding?.answers?.state,
                ...state,
            };

            if (step > 0) {
                updateUserSettings(
                    {
                        features: {
                            onBoarding: {
                                answers: {
                                    state: mergedAnswersState,
                                    step,
                                },
                                finished: done,
                            },
                        },
                    },
                    done
                );
            }

            if (done) {
                mixpanel?.track('onboarding-new-dialog', { event: 'done', answers: mergedAnswersState });
                setShouldOnBoardingDialogBeOpen(false);
                chameleon.track(ChameleonEvent.QuestionnaireDone);
            }
        },
        [state, step, chameleon]
    );

    const handleCustomInput = useCallback(
        (pickCustomInput?: { type: string; payload: Record<string, string> | string }) => {
            if (!!pickCustomInput) {
                dispatch(pickCustomInput);
                setCustomInput('');
                dispatch({ type: 'nextStep' });
            }
        },
        [setCustomInput, customInput]
    );

    useEffect(() => {
        if (step === 0) {
            mixpanel?.track('onboarding-new-dialog', { event: 'new_onboard_start' });
        }
    }, [step]);

    useEffect(() => {
        finalizeOnBoarding(!(step <= flow.length));
    }, [flow.length, step]);

    useEffect(() => {
        const step = userSettings?.features?.onBoarding?.answers?.step;
        if (step && step > 0) {
            dispatch({ type: 'loadState', payload: userSettings?.features?.onBoarding?.answers });
        }
    }, [userSettings?.features?.onBoarding?.answers]);

    const handleClick = (step: any, value: any) => {
        if (step.multiSelect) {
            if (!state[step.id]?.includes(value.id || value.label)) {
                dispatch({
                    type: step.id,
                    payload: state[step.id] ? [...state[step.id], value.id ?? value.label] : [value.id ?? value.label],
                });
            } else {
                const removed = state[step.id].filter((item: any) => item !== value.id && item !== value.label);
                dispatch({ type: step.id, payload: [...removed] });
            }
        } else {
            dispatch({ type: step.id, payload: value.id ?? value.label });
        }
    };

    return (
        <Container>
            <TopDots src={TopDotsSvg} />
            <BottomDots src={BottomDotsSvg} />
            <Header />
            <ProgressStepper show={step > 0} size={flow.length} currStep={step + 1} />
            <StepsContainer>
                <Purpose
                    show={step === 0}
                    selectedPurpose={state[OnboardingFlows.Class]}
                    setPurpose={(value: OnboardingClass | string) => dispatch({ type: OnboardingFlows.Class, payload: value })}
                    options={purposeStep.options!}
                    title={purposeStep.title}
                    onNext={() => dispatch({ type: 'nextStep' })}
                    isExistingUser={isExistingUser}
                    randomize={purposeStep.randomize}
                />
                {flow.map((thisStep, index) => (
                    <Step
                        key={thisStep.id}
                        show={step === index + 1}
                        options={(thisStep.options || []).map(value => ({
                            value,
                            onClick: () => handleClick(thisStep, value),
                            checked: thisStep.multiSelect
                                ? state[thisStep.id]?.includes(value.id ?? value.label)
                                : state[thisStep.id] === (value.id ?? value.label),
                        }))}
                        state={state}
                        step={thisStep}
                        setCustomAnswer={setCustomInput}
                        onNext={() => {
                            if (!!customInput) {
                                if (thisStep.shouldNestWithOtherSuffix) {
                                    handleCustomInput({ type: thisStep.id, payload: { [`${thisStep.id}_custom`]: customInput } });
                                } else {
                                    dispatch({ type: thisStep.id, payload: customInput });
                                    dispatch({ type: 'nextStep' });
                                }
                            } else {
                                dispatch({ type: 'nextStep' });
                            }
                        }}
                        onBack={() => dispatch({ type: 'prevStep' })}
                        last={index === flow.length - 1}
                    />
                ))}
            </StepsContainer>
        </Container>
    );
}
